/**
 * Pending-outbound map — tracks dials waiting for their server-initiated
 * A-leg to arrive as an inbound SDK notification.
 *
 * When `dial()` POSTs to /api/telephony/call, the server creates an A-leg
 * to the WebRTC browser's SIP URI with an `X-ClientCallId` custom SIP
 * header. The browser sees this as an incoming call via the TelnyxRTC
 * notification. `handleNotification` reads the custom header, looks up
 * the entry here, and auto-answers without a UI ring.
 *
 * TTL guards against orphaned entries if the server call fails or the
 * A-leg never arrives; after 30s the entry is dropped and any matching
 * inbound notification falls back to the normal inbound-ringing flow.
 */

import type { Brand, CallerInfo, ClientCallId } from './types';

export const PENDING_TTL_MS = 30_000;

export interface PendingEntry {
  clientCallId: ClientCallId;
  pstnTo: string;
  brand: Brand;
  customer: CallerInfo | null;
  startedAt: number;
}

const entries = new Map<ClientCallId, PendingEntry>();
const timers = new Map<ClientCallId, ReturnType<typeof setTimeout>>();

export function track(entry: PendingEntry): void {
  entries.set(entry.clientCallId, entry);
  const existing = timers.get(entry.clientCallId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    entries.delete(entry.clientCallId);
    timers.delete(entry.clientCallId);
  }, PENDING_TTL_MS);
  timers.set(entry.clientCallId, timer);
}

export function consumeByClientCallId(clientCallId: ClientCallId): PendingEntry | null {
  const entry = entries.get(clientCallId);
  if (!entry) return null;
  entries.delete(clientCallId);
  const timer = timers.get(clientCallId);
  if (timer) {
    clearTimeout(timer);
    timers.delete(clientCallId);
  }
  return entry;
}

export function all(): PendingEntry[] {
  return Array.from(entries.values());
}

export function clear(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  entries.clear();
  timers.clear();
}
